import { enableProdMode, signal, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { environment } from './environments/environment';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { parseAgrEnvironment, AgrEnvironment } from '@next/core-lib/environment';
import { I18nBrowserEffects, provideAgrBrowser } from '@next/core-lib/browser';
import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';
import { provideAgrCore } from '@next/core-lib/app';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { provideAgrSettings } from '@next/core-lib/settings';
import { CoreEffects } from '@next/core-lib';
import { I18nEffects } from '@next/core-lib/i18n';
import { NavigationEffects, provideAgrNavigation } from '@next/core-lib/navigation';
import { provideAgrSite, SiteEffects } from '@next/core-lib/site';
import { PWAService } from '@next/core-lib/pwa';
import { AgrWrapperSiteSidenavModule } from '@next/core-lib/wrapper-site-sidenav';
import { AgrBrandingModule } from '@next/core-lib/branding';

declare global {
  var CONFIG_BUILD_ID: string | undefined;
}

if (environment.PRODUCTION) {
  enableProdMode();
}

const loadedConfig = signal<AgrEnvironment>(environment);

export const setupRuntimeConfig = (async () => {
  try {
    const buildId = globalThis.CONFIG_BUILD_ID;
    const configUrl = buildId ? `/assets/config.json?v=${buildId}` : '/assets/config.json';
    const response = await globalThis.fetch(configUrl);
    if (response.ok) {
      const config = await response.json();
      loadedConfig.set({ ...environment, ...parseAgrEnvironment(config) });
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn('Failed to load runtime environment variables', e);
  }
})();

setupRuntimeConfig.then(() => {
  bootstrapApplication(AppComponent, {
    providers: [
      provideHttpClient(withInterceptorsFromDi()),
      importProvidersFrom(AgrBrandingModule, AgrWrapperSiteSidenavModule),
      provideAgrBrowser({ enableServiceWorker: loadedConfig().PRODUCTION }),
      provideAgrNavigation(),
      provideAgrSite(),
      PWAService,
      provideRouter(APP_ROUTES, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
      provideStore(
        {},
        {
          runtimeChecks: {
            strictStateImmutability: true,
            strictActionImmutability: true,
            strictStateSerializability: true,
            strictActionSerializability: true,
          },
        },
      ),
      provideAgrCore(),
      provideEffects(CoreEffects, I18nEffects, I18nBrowserEffects, NavigationEffects, SiteEffects),
      provideAgrRouterStore(),
      provideAgrSettings(),
      {
        provide: ENVIRONMENT_MAPPER,
        useFactory: () => loadedConfig(),
      },
    ],
  }).catch((err) => console.error(err));
});
